import React, { FC, useCallback, useMemo, useState } from 'react'
import { format } from 'date-fns'
import styles from 'styles/components/card.module.scss'
import { Caution } from 'components/Typography'
import { OrderType } from 'types/checkout'
import { Spacer } from 'components/Utility'
import { FlexRow } from 'components/Flex'
import { Checkbox } from 'components/Form'
import { OrangeButton } from 'components/Button'
import { useSelector } from 'react-redux'
import { State } from 'index'
import { getRole, getStoreRef, getUid } from 'reducks/auth/selectors'
import { toggleDelivered, togglePrepared } from 'lib/order'
import { OrderCardBodySummary, OrderCardBodyItems, OrderCardCancel } from 'components/Card'
import { Role } from 'lib/role'
import { sumOrdersPrice } from 'lib/revenue'
import { useCanBeCanceled, useCancelDeadline, useOrderLabel } from 'hooks/order'
import { getLastPickupTime } from 'reducks/store/selectors'
import { BusinessTimeType } from 'types/store'
import { getSpecialHolidays } from 'reducks/calendar/selectors'
import { DaysOfWeek } from 'lib/daysOfWeek'
import { parseTimeToDate, parseToDate } from 'lib/helper'
import { getBusinessTimeOnDay } from 'lib/businessHour'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
const daysOfWeek = new DaysOfWeek()

type Props = {
  isSearch?: boolean
  order: OrderType
  date?: string
}

const OrderCardBody: FC<Props> = (props) => {
  const { isSearch, order, date } = props

  const role = useSelector((state: State) => getRole(state))
  const lastPickupTime: BusinessTimeType = useSelector((state: State) => getLastPickupTime(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const uid = useSelector((state: State) => getUid(state))
  const roleManager = new Role(role, storeRef)
  const dayOfWeek = daysOfWeek.getDayOfWeek(parseToDate(order.date), specialHolidays)
  const lastPickupTimeOnDay =
    getBusinessTimeOnDay(dayOfWeek, lastPickupTime, order.date, storeCalendars, 'last_pickup_time') ?? '23:45'

  const [isDelivered, setDelivered] = useState<boolean>(order.is_delivered)
  const [isPrepared, setPrepared] = useState<boolean>(order.is_prepared)
  // 注文横断検索時はクエリしない
  const cancelDeadline = isSearch
    ? useMemo(() => new Date(), [])
    : useCancelDeadline(
        order.checkout_at ? order.checkout_at.toDate() : order.updated_at.toDate(),
        parseTimeToDate(lastPickupTimeOnDay, parseToDate(order.date)),
        order.payment_method,
        uid
      )
  const canBeCanceled = isSearch ? false : useCanBeCanceled(cancelDeadline, isDelivered, order.status, order.type)

  const handlePrepared = useCallback(
    (checkoutId: string) => {
      setPrepared((prevState) => {
        togglePrepared(checkoutId, !prevState, storeRef)
        return !prevState
      })
    },
    [setPrepared, storeRef]
  )

  const handleDelivered = useCallback(
    (checkoutId: string) => {
      setDelivered((prevState) => {
        toggleDelivered(checkoutId, !prevState, storeRef)
        return !prevState
      })
    },
    [setDelivered, storeRef]
  )

  const orderLabel = useOrderLabel(order.checkout_at, order.updated_at)

  return (
    <div className={styles.order__body}>
      {order.status === 'canceled' && (
        <>
          <FlexRow justifyContent={'center'}>
            <Caution caution={'この注文はキャンセルされています'} dataCy={'canceled'} size={'large'} />
          </FlexRow>
          <Spacer size={16} />
        </>
      )}
      <FlexRow justifyContent={'between'}>
        <FlexRow justifyContent={'start'}>
          <img src={'/icon/grey/clock.svg'} alt={'来店予定時刻'} />
          <Spacer size={5} />
          <span className={styles.order__body__pickup__label}>来店予定時刻：</span>
          {isSearch && date ? (
            <span className={styles.order__body__pickup__time}>
              {format(parseToDate(date), 'yyyy年MM月dd日') + ' ' + order.pickup_time}
            </span>
          ) : (
            <span className={styles.order__body__pickup__time}>{order.pickup_time}</span>
          )}
        </FlexRow>
        <Checkbox
          dataCy={`status-準備済`}
          disabled={isDelivered || !roleManager.hasEnoughRole(5) || order.status === 'canceled'}
          id={order.checkout_id ?? ''}
          isChecked={isPrepared}
          label={'準備済'}
          handleCheckbox={handlePrepared}
        />
      </FlexRow>
      <Spacer size={8} />
      <OrderCardBodyItems items={order.items} />
      <Spacer size={8} />
      <OrderCardBodySummary
        amount={sumOrdersPrice([order])}
        checkoutChannel={order.checkout_channel}
        discountAmount={order.discount_amount || 0}
        orderLabel={orderLabel}
        orderType={order.type}
        request={order.request}
        shoppingBags={order.shopping_bags}
      />
      <Spacer size={16} />
      <OrangeButton
        disabled={!isPrepared || isDelivered || !roleManager.hasEnoughRole(5) || order.status === 'canceled'}
        label={isDelivered ? '受渡済です' : 'この注文を受渡済にする'}
        onClick={() => handleDelivered(order.checkout_id ?? '')}
        size={'medium'}
        type={'main'}
      />
      {canBeCanceled && <OrderCardCancel cancelDeadline={cancelDeadline} checkoutId={order.checkout_id ?? ''} />}
    </div>
  )
}

export default OrderCardBody
